import * as THREE from "three";
import { VolumeMetadata } from "@/swagger";
import { NexusObject } from "../../external/nexus_three";
import { MeasurementType } from "../data-structures/measure-type";
import { ViewType } from "../data-structures/view-type";
import { Tools } from "./tools";
import { getViewerColorFromId } from "./viewer-color-utils";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { Line2 } from "three/examples/jsm/lines/Line2";

declare const Potree: any;
declare const proj4: any;

/**
 * Manager responsible for managing 3D volume visualizations including
 * heatmaps, polygon projections, and volume measurements.
 */
export class VolumeManager {
	// Constants
	private static readonly TOOLTIP_UPDATE_THROTTLE_MS = 80;
	private static readonly MAX_PROJECTION_RETRIES = 10;
	private static readonly PROJECTION_RETRY_BASE_DELAY_MS = 250;

	private tooltipEl: HTMLDivElement | null = null;
	private lastTooltipUpdate = 0;
	private readonly hoverCursor = "url(\"data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%3E%3Cpolygon%20points%3D%226%2C1%2011%2C11%201%2C11%22%20fill%3D%22none%22%20stroke%3D%22white%22%20stroke-width%3D%221.5%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E\") 6 1, default";
	private cursorOverride: string | null = null;
	private hoveredVolumeId: string | null = null;
	public volumes: any[] = [];

	public constructor(
		private readonly viewer: any,
		private readonly domElement: HTMLDivElement
	) {
		this.createTooltip();
	}

	public getCursorOverride(): string | null {
		return this.cursorOverride;
	}

	public configureDiffRenderState(diffObject: THREE.Object3D | null, show: boolean): void {
		if (!diffObject) return;
		diffObject.visible = show;
		const mat: any = (diffObject as any).material;
		if (mat && show) {
			mat.transparent = true;
			mat.depthWrite = false;
			mat.opacity = 1;
			mat.needsUpdate = true;
		}
		(diffObject.userData || (diffObject.userData = {})).diffVisible = show;
	}

	public getChildVisibility(volumeId: string, childType: "before" | "after" | "diff"): boolean {
		const volumeGroup = this.volumes.find((v: any) => v.uuid === volumeId);
		if (!volumeGroup) return true;

		const state = (volumeGroup as any).userData?.visibility;
		if (state && Object.prototype.hasOwnProperty.call(state, childType)) {
			return state[childType];
		}

		const targets = this.getBeforeAfterTargets(volumeGroup);
		const child = targets?.[childType];
		if (!child) return false;

		if (childType === "diff") {
			const diffVisible = child.userData?.diffVisible;
			return child.visible && (diffVisible !== false);
		}

		return child.visible;
	}

	public toggleChildVisibility(volumeId: string, childType: "before" | "after" | "diff"): void {
		const volumeGroup = this.volumes.find((v: any) => v.uuid === volumeId);
		if (!volumeGroup) return;

		const currentState = (volumeGroup as any).userData?.visibility || {
			before: true,
			after: true,
			diff: false
		};

		const newState = {
			...currentState,
			[childType]: !currentState[childType]
		};

		(volumeGroup as any).userData = (volumeGroup as any).userData || {};
		(volumeGroup as any).userData.visibility = newState;

		const targets = this.getBeforeAfterTargets(volumeGroup);
		if (targets.before) targets.before.visible = newState.before;
		if (targets.after) targets.after.visible = newState.after;
		if (targets.diff) {
			targets.diff.visible = newState.diff;
			targets.diff.userData = targets.diff.userData || {};
			targets.diff.userData.diffVisible = newState.diff;
		}
	}

	public syncViewType(viewType: ViewType): void {
		for (const volume of this.volumes) {
			const group = volume as any;
			const targets = this.getBeforeAfterTargets(group);
			const state = group?.userData?.visibility;

			if (state) {
				if (targets.before) (targets.before as any).visible = !!state.before;
				if (targets.after) (targets.after as any).visible = !!state.after;
				this.configureDiffRenderState(targets.diff, !!state.diff);
			} else {
				if (targets.before) (targets.before as any).visible = true;
				if (targets.after) (targets.after as any).visible = true;
				this.configureDiffRenderState(targets.diff, false);
			}

			volume.children?.forEach((child: any) => {
				if (child.name === "SelectionPolygon") {
					child.visible = viewType == ViewType.View2D;
				}
				if (child.name === "SelectionConnectors") {
					child.visible = viewType == ViewType.View3D;
					child.traverse((obj: any) => {
						if (obj.material && obj.material.resolution) {
							const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
							obj.material.resolution.set(renderAreaSize.width, renderAreaSize.height);
							obj.material.needsUpdate = true;
						}
					});
				}
				if (child.name === "SelectionProjections") {
					child.visible = viewType == ViewType.View3D;
				}
			});

			if (viewType === ViewType.View3D) {
				const hasConnectors = volume.children?.find((c: any) => c.name === "SelectionConnectors");
				const hasProjectionData = ((volume as any).userData?.projectedBasePoints?.length ?? 0) > 0;
				if (!hasConnectors && hasProjectionData) {
					this.buildProjectionLines(volume as THREE.Group);
				}
			}
		}
	}

	public convert(toMapCoordinates: boolean): void {
		if (!this.viewer.projectCoordinateSystem) return;

		for (const volume of this.volumes) {
			const group = volume as THREE.Group;
			const isInMap = (group as any).userData?.hasMapCoordinates === true;
			if (isInMap === toMapCoordinates) continue;

			const proj = proj4(this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem);
			if (toMapCoordinates) {
				const poly = group.children?.find(c => c.name === "SelectionPolygon") as THREE.Group | undefined;
				const markers: THREE.Vector3[] = (poly as any)?.userData?.markerPositionsLocal as THREE.Vector3[];
				const hasMarkers = Array.isArray(markers) && markers.length >= 2;
				const posMap = proj.forward([group.position.x, group.position.y, group.position.z]);
				let theta = 0, s = 1, tx = 0, ty = 0;
				if (hasMarkers) {
					const src: Array<[number, number]> = markers.map(v => [v.x, v.y]);
					const dst: Array<[number, number]> = markers.map(v => {
						const w = proj.forward([group.position.x + v.x, group.position.y + v.y, group.position.z]);
						return [w[0] - posMap[0], w[1] - posMap[1]];
					});
					const muS = src.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
					muS[0] /= src.length; muS[1] /= src.length;
					const muD = dst.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
					muD[0] /= dst.length; muD[1] /= dst.length;
					let a = 0, b = 0, denom = 0;
					for (let i = 0; i < src.length; i++) {
						const x = src[i][0] - muS[0];
						const y = src[i][1] - muS[1];
						const u = dst[i][0] - muD[0];
						const v = dst[i][1] - muD[1];
						a += x * u + y * v;
						b += x * v - y * u;
						denom += x * x + y * y;
					}
					if (denom > 0) {
						theta = Math.atan2(b, a);
						s = Math.sqrt(a * a + b * b) / denom;
						const cos = Math.cos(theta), sin = Math.sin(theta);
						tx = muD[0] - s * (cos * muS[0] - sin * muS[1]);
						ty = muD[1] - s * (sin * muS[0] + cos * muS[1]);
					}
				} else {
					const ref = group.position.clone();
					s = this.computeLocalScaleFactor(ref, this.viewer.projectCoordinateSystem, this.viewer.mapCoordinateSystem);
				}

				group.position.set(posMap[0] + tx, posMap[1] + ty, posMap[2]);
				if (Number.isFinite(s) && s > 0) {
					group.scale.set(group.scale.x * s, group.scale.y * s, group.scale.z);
				}
				group.rotation.z += theta;
				(group as any).userData.__mapSim = { theta, s, tx, ty };
			} else {
				const sStored = (group as any).userData?.__scaleToMap;
				const p = proj.inverse([group.position.x, group.position.y, group.position.z]);
				group.position.set(p[0], p[1], p[2]);
				const sim = (group as any).userData?.__mapSim;
				if (sim && Number.isFinite(sim.s) && sim.s > 0) {
					const inv = 1 / sim.s;
					group.scale.set(group.scale.x * inv, group.scale.y * inv, group.scale.z);
					group.rotation.z -= sim.theta || 0;
					(group as any).userData.__mapSim = undefined;
				} else if (Number.isFinite(sStored) && sStored > 0) {
					const inv = 1 / sStored;
					group.scale.set(group.scale.x * inv, group.scale.y * inv, group.scale.z);
					(group as any).userData.__scaleToMap = undefined;
				}
			}

			group.updateMatrixWorld(true);
			this.syncProjectionCache(group);
			(group as any).userData.hasMapCoordinates = toMapCoordinates;
		}
	}

	public updateLabelScales(camera: any, clientWidth: number, clientHeight: number): void {
		for (const volume of this.volumes) {
			if (volume.children) {
				for (const child of volume.children) {
					if (child.name === "SelectionPolygon" && child.userData.areaLabel) {
						const label = child.userData.areaLabel;
						const distance = label.position.distanceTo(camera.position);
						const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
						const scale = (50 / pr);
						label.scale.set(scale, scale, scale);
					}
					if (child.name === "SelectionConnectors") {
						child.traverse((obj: any) => {
							if (obj.material && obj.material.resolution) {
								obj.material.resolution.set(clientWidth, clientHeight);
							}
						});
					}
				}
			}

			if (volume.userData && volume.userData.areaLabel3D) {
				const label3D = volume.userData.areaLabel3D;
				label3D.visible = this.viewer.viewType == ViewType.View3D;
				const distance = camera.position.distanceTo(label3D.getWorldPosition(new THREE.Vector3()));
				const pr = Potree.Utils.projectedRadius(1, camera, distance, clientWidth, clientHeight);
				const scale = (70 / pr);
				label3D.scale.set(scale, scale, scale);
				if (label3D.material && typeof label3D.material.depthTest === "boolean") {
					label3D.material.depthTest = false;
					label3D.material.depthWrite = false;
				}
				label3D.renderOrder = 999999;
				label3D.traverse((child: any) => {
					if (child.type === "Sprite" || child.isSprite) {
						child.renderOrder = 999999;
						if (child.material) {
							child.material.depthTest = false;
							child.material.depthWrite = false;
						}
					}
				});
			}
		}
	}

	public async intersectVisibleMeshes(raycaster: THREE.Raycaster): Promise<THREE.Vector3 | null> {
		for (const volume of this.volumes) {
			let intersection = null;
			const { before, after } = this.getBeforeAfterTargets(volume);

			if (after && after.visible) {
				intersection = await (after as NexusObject).raycast(raycaster);
			} else if (before && before.visible) {
				intersection = await (before as NexusObject).raycast(raycaster);
			}

			if (intersection) return intersection;
		}

		return null;
	}

	public async load(url: string, name: string, uuid: string, visible = false, files: string[], metadata: VolumeMetadata): Promise<any> {
		try {
			console.log("Loading volume:", url, name);

			const nxsAfterUrl = files.find(f => /(^|\/)after\.nxs$/i.test(f) || /after\.nxs/i.test(f));
			const nxsBeforeUrl = files.find(f => /(^|\/)before\.nxs$/i.test(f) || /before\.nxs/i.test(f));
			const nxsDiffUrl = files.find(f => /(^|\/)diff\.nxs$/i.test(f) || /diff\.nxs/i.test(f));
			const jsBeforeUrl = files.find(f => f.includes("before.js"));
			const jsAfterUrl = files.find(f => f.includes("after.js"));
			const jsDiffUrl = files.find(f => f.includes("diff.js"));

			const loadSingleVolume = async (
				nxsUrl: string | undefined,
				jsUrl: string | undefined,
				volumeName: string,
			): Promise<any> => {
				if (!nxsUrl || !jsUrl) return null;
				const response = await fetch(jsUrl);
				const transformation = await response.json();

				return new Promise((resolve) => {
					const volumeMaterial = new THREE.MeshMatcapMaterial({ flatShading: true });
					const volume = new NexusObject(
						nxsUrl,
						function () {
							volume.visible = visible;
							resolve(volume);
						},
							function () {
								// Nexus reload callback intentionally unused.
							},
						this.viewer.renderer,
						volumeMaterial,
						this.viewer.scene.scene,
						this.viewer.scene.getActiveCamera()
					);

					if (this.viewer.convertToWGS84) {
						const newVolumePosition = proj4(
							this.viewer.projectCoordinateSystem,
							this.viewer.mapCoordinateSystem
						).forward([
							volume.position.x,
							volume.position.y,
							volume.position.z,
						]);
						volume.position.set(newVolumePosition[0], newVolumePosition[1], newVolumePosition[2]);
					}

					volume.name = volumeName;
					volume.position.set(
						transformation.origin[0],
						transformation.origin[1],
						transformation.origin[2]
					);

					this.viewer.meshManager.preloadTextures(volume);
					this.viewer.meshManager.computeRaycastBVH(volume);
					volume.geometry.computeVertexNormals();
					volume.frustumCulled = false;
				});
			};

			const beforeVolume = await loadSingleVolume(nxsBeforeUrl || url, jsBeforeUrl, metadata.name || "Volume");
			const afterVolume = await loadSingleVolume(nxsAfterUrl, jsAfterUrl, metadata.name || "Volume");
			const diffVolume = await loadSingleVolume(nxsDiffUrl, jsDiffUrl, metadata.name || "Volume");

			const volumeGroup = new THREE.Group();
			volumeGroup.name = metadata.name || "Volume";
			volumeGroup.uuid = uuid;
			volumeGroup.visible = visible;
			(volumeGroup as any).userData = (volumeGroup as any).userData || {};
			(volumeGroup as any).userData.metadata = metadata;

			if (beforeVolume) volumeGroup.add(beforeVolume);
			if (afterVolume) volumeGroup.add(afterVolume);
			if (diffVolume) volumeGroup.add(diffVolume);

			if (beforeVolume) {
				(beforeVolume as any).userData = (beforeVolume as any).userData || {};
				(beforeVolume as any).userData.volumeType = "before";
			}
			if (afterVolume) {
				(afterVolume as any).userData = (afterVolume as any).userData || {};
				(afterVolume as any).userData.volumeType = "after";
			}
			if (diffVolume) {
				(diffVolume as any).userData = (diffVolume as any).userData || {};
				(diffVolume as any).userData.volumeType = "diff";
				this.configureDiffRenderState(diffVolume, false);
			}

			this.viewer.volumesGroup.add(volumeGroup);
			this.volumes.push(volumeGroup as any);
			this.viewer.updateLayers();
			this.viewer.updateGroundPosition();

			const measurementGroup = new THREE.Group();
			measurementGroup.name = "SelectionPolygon";
			measurementGroup.visible = this.viewer.viewType == ViewType.View2D;
			measurementGroup.userData.color = this.getDefaultColor(uuid);
			measurementGroup.userData.unit = this.viewer.unit;
			measurementGroup.userData.projectCoordinateSystem = this.viewer.projectCoordinateSystem;
			measurementGroup.userData.hasMapCoordinates = this.viewer.viewType == ViewType.View2D;
			measurementGroup.userData.displayedMeasurements = [MeasurementType.Area3D];

			const markerPositions = (metadata.selection_polygon || [])
				.map((e: any) => {
					const x = typeof e?.[0] === "string" ? parseFloat(e[0]) : e?.[0];
					const y = typeof e?.[1] === "string" ? parseFloat(e[1]) : e?.[1];
					if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
					return new THREE.Vector3(x, y, beforeVolume ? beforeVolume.position.z : (afterVolume ? afterVolume.position.z : 0));
				})
				.filter((v: any) => v);
			measurementGroup.userData.markerPositionsLocal = markerPositions.map((v: THREE.Vector3) => v.clone());

			for (let i = 0; i < markerPositions.length; i++) {
				const nextIndex = (i + 1) % markerPositions.length;
				const lineGeometry = new LineGeometry();
				lineGeometry.setPositions([
					0, 0, 0,
					...markerPositions[nextIndex].clone().sub(markerPositions[i]).toArray(),
				]);
				const lineMaterial = new LineMaterial({
					color: 0xffffff,
					linewidth: 2,
					resolution: new THREE.Vector2(1000, 1000),
				});
				lineMaterial.depthTest = false;
				const edge = new Line2(lineGeometry, lineMaterial);
				edge.position.copy(markerPositions[i]);
				edge.visible = true;
				measurementGroup.add(edge);
			}

			if (markerPositions.length >= 3) {
				const centroid = markerPositions.reduce((acc: THREE.Vector3, v: THREE.Vector3) => acc.add(v), new THREE.Vector3()).divideScalar(markerPositions.length);
				const areaLabel = this.createVolumeLabel(metadata.name || "Volume");
				areaLabel.visible = true;
				areaLabel.renderOrder = 10050;
				areaLabel.position.copy(centroid);
				measurementGroup.add(areaLabel);
				measurementGroup.userData.areaLabel = areaLabel;

				const areaLabel3D = this.createVolumeLabel(metadata.name || "Volume");
				areaLabel3D.visible = this.viewer.viewType == ViewType.View3D;
				areaLabel3D.renderOrder = 999999;
				areaLabel3D.traverse((child: any) => {
					if (child.type === "Sprite" || child.isSprite) {
						child.renderOrder = 999999;
						if (child.material) {
							child.material.depthTest = false;
							child.material.depthWrite = false;
						}
					}
				});

				const meshHeight = metadata.diffZMax - metadata.diffZMin;
				if (beforeVolume) beforeVolume.position.z += meshHeight;
				if (afterVolume) afterVolume.position.z += meshHeight;
				measurementGroup.position.z += meshHeight;

				(volumeGroup as any).userData = (volumeGroup as any).userData || {};
				(volumeGroup as any).userData.raiseOffset = meshHeight;

				const aboveCentroid = centroid.clone();
				aboveCentroid.z += meshHeight;
				areaLabel3D.position.copy(aboveCentroid);
				volumeGroup.add(areaLabel3D);
				volumeGroup.userData.areaLabel3D = areaLabel3D;
			}

			volumeGroup.add(measurementGroup);

			if (markerPositions.length >= 3) {
				const centroid = markerPositions.reduce((acc: THREE.Vector3, v: THREE.Vector3) => acc.add(v), new THREE.Vector3()).divideScalar(markerPositions.length);
				const pivot = new THREE.Vector3(centroid.x, centroid.y, 0);
				(volumeGroup as any).userData = (volumeGroup as any).userData || {};
				(volumeGroup as any).userData.pivot = pivot.clone();

				for (const child of [beforeVolume, afterVolume, diffVolume]) {
					if (!child) continue;
					child.position.sub(pivot);
				}
				for (const child of measurementGroup.children) {
					(child as any).position && (child as any).position.sub(pivot);
				}
				if ((volumeGroup as any).userData?.areaLabel3D && (volumeGroup as any).userData.areaLabel3D.position) {
					(volumeGroup as any).userData.areaLabel3D.position.sub(pivot);
				}
				const localMarkers = markerPositions.map((p: THREE.Vector3) => p.clone().sub(pivot));
				measurementGroup.userData.markerPositionsLocal = localMarkers.map((v: THREE.Vector3) => v.clone());
				volumeGroup.position.add(pivot);
			}

			this.applyOverlayColor(volumeGroup, measurementGroup.userData.color);

			if ((volumeGroup as any).userData?.areaLabel3D) {
				const label3D = (volumeGroup as any).userData.areaLabel3D;
				volumeGroup.updateMatrixWorld(true);
				const worldPos = label3D.getWorldPosition(new THREE.Vector3());
				const worldQuat = label3D.getWorldQuaternion(new THREE.Quaternion());
				const worldScale = label3D.getWorldScale(new THREE.Vector3());
				this.viewer.volumeLabelsGroup.add(label3D);
				label3D.position.copy(worldPos);
				label3D.quaternion.copy(worldQuat);
				label3D.scale.copy(worldScale);
			}

			try {
				const localMarkers = (measurementGroup.userData?.markerPositionsLocal || markerPositions) as THREE.Vector3[];
				await this.projectPolygonToUnderlyingMesh(volumeGroup, localMarkers);
				this.buildProjectionLines(volumeGroup);
				const attachUpdateHook = (obj: any) => {
					if (!obj || !obj.geometry || !obj.geometry.instance) return;
					const inst = obj.geometry.instance;
					let pending = false;
					const prev = inst.onUpdate;
					inst.onUpdate = (...args: any[]) => {
						try { if (typeof prev === "function") prev.apply(inst, args); } catch { console.error("Error in onUpdate:", args); }
						if (pending) return;
						pending = true;
						setTimeout(async () => {
							pending = false;
							const localMarkers = (measurementGroup.userData?.markerPositionsLocal || markerPositions) as THREE.Vector3[];
							await this.projectPolygonToUnderlyingMesh(volumeGroup, localMarkers);
							this.buildProjectionLines(volumeGroup);
						}, 250);
					};
				};
				attachUpdateHook(beforeVolume);
				attachUpdateHook(afterVolume);
				attachUpdateHook(diffVolume);
			} catch (err) {
				console.warn("Failed to project volume polygon:", err);
			}

			if (this.viewer.viewType === ViewType.View2D) {
				this.viewer.convertMeasurements(true);
			}

			return volumeGroup;
		} catch (e: unknown) {
			console.error("Error loading volume:", e);
			return null;
		}
	}

	public setOverlayColor(volumeId: string, color: string | number | THREE.Color): void {
		const volume = this.volumes.find((v: any) => v.uuid === volumeId);
		if (!volume) {
			console.warn("Volume not found:", volumeId);
			return;
		}
		this.applyOverlayColor(volume as THREE.Group, color);
	}

	public isHeatmapEnabled(volumeId: string): boolean {
		const volume = this.volumes.find((v: any) => v.uuid === volumeId);
		if (!volume) return false;
		return (volume as any).userData?.heatmapEnabled === true;
	}

	public async setHeatmapEnabled(volumeId: string, enabled: boolean): Promise<void> {
		const volume = this.volumes.find((v: any) => v.uuid === volumeId);
		if (!volume) {
			console.warn("Volume not found:", volumeId);
			return;
		}

		const targets = this.getBeforeAfterTargets(volume as any);
		const volumeGroup = volume as any;
		volumeGroup.userData = volumeGroup.userData || {};
		volumeGroup.userData.heatmapEnabled = enabled;

		if (enabled) {
			this.configureDiffRenderState(targets.diff, false);
			volumeGroup.userData.visibility = {
				...volumeGroup.userData.visibility,
				before: true,
				after: true,
				diff: false
			};
		} else {
			const solidColorHex = volumeGroup.userData?.solidColorHex;
			if (solidColorHex !== undefined) {
				const targetColor = new THREE.Color(solidColorHex);
				if ((targets.before as any).isMesh) {
					(targets.before as any).material = new THREE.MeshLambertMaterial({ color: targetColor });
				}
				if ((targets.after as any).isMesh) {
					(targets.after as any).material = new THREE.MeshLambertMaterial({ color: targetColor });
				}
			}

			if (volumeGroup.userData?.solidColorHex !== undefined) {
				this.applyOverlayColor(volumeGroup, volumeGroup.userData.solidColorHex);
			}

			this.configureDiffRenderState(targets.diff, false);
			volumeGroup.userData.visibility = {
				...volumeGroup.userData.visibility,
				diff: false
			};
		}

		await this.refreshActiveHeatmapColors();
		this.viewer.render();
		if (enabled) {
			for (let i = 0; i < 5; i++) {
				await new Promise(resolve => window.setTimeout(resolve, 50 + i * 50));
				await this.refreshActiveHeatmapColors().catch((error) => { console.warn("Failed to refresh heatmap colors:", error); });
				this.viewer.render();
			}
		}
	}

	public async rebuildAllConnectors(): Promise<void> {
		if (!this.volumes || this.volumes.length === 0) return;
		if (!this.viewer.meshes || this.viewer.meshes.length === 0) return;

		this.viewer.scene.scene.updateMatrixWorld(true);
		this.viewer.volumesGroup.updateMatrixWorld(true);
		this.viewer.meshesGroup.updateMatrixWorld(true);

		for (const vol of this.volumes) {
			const poly = vol.children?.find((c: any) => c.name === "SelectionPolygon") as THREE.Group;
			if (!poly) continue;

			const vertices: THREE.Vector3[] = (poly.userData?.markerPositionsLocal || []) as THREE.Vector3[];
			if (vertices.length === 0) continue;

			const storedIndexedWorld = (vol as any).userData?.projectedBasePointsIndexed as (THREE.Vector3 | null)[] | undefined;
			const storedIndexedLocal = (vol as any).userData?.projectedBasePointsIndexedLocal as (THREE.Vector3 | null)[] | undefined;
			const hasWorldComplete =
				Array.isArray(storedIndexedWorld) &&
				storedIndexedWorld.length >= vertices.length &&
				vertices.every((_, idx) => storedIndexedWorld[idx] instanceof THREE.Vector3);
			const hasLocalComplete =
				Array.isArray(storedIndexedLocal) &&
				storedIndexedLocal.length >= vertices.length &&
				vertices.every((_, idx) => storedIndexedLocal[idx] instanceof THREE.Vector3);
			const hasCompleteBasePoints = hasWorldComplete || hasLocalComplete;
			const hasConnectors = vol.children?.find((c: any) => c.name === "SelectionConnectors");

			if (hasConnectors && hasCompleteBasePoints) continue;

			await this.projectPolygonToUnderlyingMesh(vol as any, vertices);
			this.buildProjectionLines(vol as any);
		}
	}

	public remove(volume: any): void {
		if (volume) {
			this.clearProjectionRetry(volume as THREE.Group);
		}
		if (this.viewer.scene.removeVolume) {
			this.viewer.scene.removeVolume(volume);
		}
		const index = this.volumes.indexOf(volume);
		if (index > -1) {
			this.volumes.splice(index, 1);
		}

		if (volume.children) {
			for (const child of volume.children) {
				if (child.isNexusObject && typeof child.dispose === "function") {
					try {
						child.dispose();
					} catch(err) {
						console.warn("Error disposing Nexus volume mesh:", err);
					}
				}
			}
		}

		if (volume.boundaryPolygon) {
			if (volume.boundaryPolygon.parent) {
				volume.boundaryPolygon.parent.remove(volume.boundaryPolygon);
			}
			volume.boundaryPolygon = null;
		}

		this.viewer.updateLayers();
	}

	public cleanup(): void {
		while (this.volumes.length > 0) {
			this.remove(this.volumes[0]);
		}
	}

	/**
	 * Get bounding box for a volume object by using its "before" child
	 */
	public getBoundingBoxForVolume(volumeObj: any): THREE.Box3 | null {
		if (!volumeObj || volumeObj.parent?.name !== "VolumesGroup") {
			return null;
		}

		const beforeChild = volumeObj.children.find((c: any) => c.userData.volumeType === "before");
		if (!beforeChild) {
			return null;
		}

		try {
			beforeChild.updateMatrixWorld(true);
			return new THREE.Box3().setFromObject(beforeChild);
		} catch {
			return null;
		}
	}

	public getBeforeAfterTargets(volumeGroup: THREE.Group): { before: any; after: any; diff: any } {
		let before = null, after = null, diff = null;

		for (const child of volumeGroup.children) {
			const childAny = child as any;
			if (childAny.userData?.volumeType === "before") before = child;
			else if (childAny.userData?.volumeType === "after") after = child;
			else if (childAny.userData?.volumeType === "diff") diff = child;
		}

		return { before, after, diff };
	}

	public syncProjectionCache(volumeGroup: THREE.Group): void {
		const userData: any = (volumeGroup as any).userData;
		if (!userData) return;

		const localIndexed: (THREE.Vector3 | null)[] = Array.isArray(userData.projectedBasePointsIndexedLocal)
			? userData.projectedBasePointsIndexedLocal as (THREE.Vector3 | null)[]
			: [];
		if (!Array.isArray(localIndexed) || localIndexed.length === 0) return;

		volumeGroup.updateMatrixWorld(true);
		const worldIndexed = localIndexed.map((p) => {
			if (p instanceof THREE.Vector3) {
				return volumeGroup.localToWorld(p.clone());
			}
			return null;
		});
		const worldPoints = worldIndexed
			.filter((p): p is THREE.Vector3 => !!p)
			.map(p => p.clone());

		if (worldPoints.length > 0) {
			userData.projectedBasePoints = worldPoints.map(p => p.clone());
		} else {
			delete userData.projectedBasePoints;
		}

		userData.projectedBasePointsIndexed = worldIndexed.map(p => (p ? p.clone() : null));
		userData.projectedBasePointsComplete = worldIndexed.length > 0 && worldIndexed.every(p => p instanceof THREE.Vector3);
	}

	private createVolumeLabel(text: string): any {
		const label = new Potree.TextSprite();
		label.setTextColor({r: 255, g: 255, b: 255, a: 1.0});
		label.setBorderColor({r: 0, g: 0, b: 0, a: 1.0});
		label.setBackgroundColor({r: 0, g: 0, b: 0, a: 1.0});
		label.fontsize = 16;
		label.material.depthTest = false;
		label.material.transparent = true;
		(label.material as any).depthWrite = false;
		label.material.opacity = 1;
		label.setText(text);
		return label;
	}

	private getDefaultColor(uuid: string): number {
		const colorHex = getViewerColorFromId(uuid);
		const c = new THREE.Color(colorHex as any);
		return c.getHex();
	}

	private applyOverlayColor(volumeGroup: THREE.Group, color: string | number | THREE.Color): void {
		const targetColor = (color instanceof THREE.Color) ? color : new THREE.Color(color as any);
		const poly = volumeGroup.children?.find(c => c.name === "SelectionPolygon") as THREE.Group;
		if (!poly) return;

		(poly.userData || (poly.userData = {})).color = targetColor.getHex();
		const area = poly.getObjectByName("area") as THREE.Mesh;
		if (area && (area.material as any)?.color) {
			(area.material as any).color.set(targetColor);
			(area.material as any).needsUpdate = true;
		}

		(volumeGroup as any).userData = (volumeGroup as any).userData || {};
		(volumeGroup as any).userData.solidColorHex = targetColor.getHex();
		const heatmapActive = (volumeGroup as any).userData?.heatmapEnabled === true;
		if (!heatmapActive) {
			for (const child of volumeGroup.children) {
				const anyChild: any = child as any;
				if ((anyChild?.userData?.volumeType === "diff")) continue;
				if (anyChild.isMesh && child.name !== "SelectionPolygon" && anyChild.material) {
					anyChild.material = new THREE.MeshLambertMaterial({ color: targetColor });
				}
			}
		}
		this.viewer.render();
	}

	private resetHeatmapMesh(mesh: any): void {
		if (!mesh || !mesh.material) return;
		const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
		for (const mat of materials) {
			if (!mat) continue;
			if ((mat as any).color) {
				(mat as any).color.set(0xffffff);
			}
			if ("vertexColors" in mat) {
				(mat as any).vertexColors = true;
			}
			(mat as any).needsUpdate = true;
		}
	}

	private async refreshActiveHeatmapColors(): Promise<void> {
		const activeVolumes = this.volumes.filter((v: any) => v?.userData?.heatmapEnabled);
		if (activeVolumes.length === 0) return;

		let globalMin = Number.POSITIVE_INFINITY;
		let globalMax = Number.NEGATIVE_INFINITY;

		for (const volume of activeVolumes) {
			const metadata = (volume as any).userData?.metadata;
			const volMin = metadata?.diffZMin;
			const volMax = metadata?.diffZMax;
			if (typeof volMin === "number" && Number.isFinite(volMin) && volMin < globalMin) {
				globalMin = volMin;
			}
			if (typeof volMax === "number" && Number.isFinite(volMax) && volMax > globalMax) {
				globalMax = volMax;
			}
		}

		if (!Number.isFinite(globalMin) || !Number.isFinite(globalMax)) return;
		const range: [number, number] = [globalMin, globalMax];

		for (const volume of activeVolumes) {
			const { before, after, diff } = this.getBeforeAfterTargets(volume as THREE.Group);
			const ensureHeatmapMaterial = (m: any) => {
				if (!m || !m.isMesh) return;
				const isBasic = m.material && m.material.isMeshBasicMaterial;
				const hasVertexColors = !!(m.material && m.material.vertexColors);
				const needChange = !hasVertexColors || !isBasic;
				if (needChange) {
					const mat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
					(mat as any).toneMapped = false;
					m.material = mat;
					m.material.needsUpdate = true;
				}
			};
			if (before) ensureHeatmapMaterial(before);
			if (after) ensureHeatmapMaterial(after);
			if (before) this.resetHeatmapMesh(before);
			if (after) this.resetHeatmapMesh(after);
			if (diff) this.configureDiffRenderState(diff, false);
			try {
				if (before && diff) {
					await (before as any).applyDiffColors(diff, { range, mode: "signed" });
				}
			} catch (err) {
				console.warn("Failed to apply heatmap colors to before mesh", err);
			}
			try {
				if (after && diff) {
					await (after as any).applyDiffColors(diff, { range, mode: "signed" });
				}
			} catch (err) {
				console.warn("Failed to apply heatmap colors to after mesh", err);
			}
		}
	}

	private async projectPolygonToUnderlyingMesh(
		volumeGroup: THREE.Group,
		markerPositions: THREE.Vector3[],
		attempt = 0,
	): Promise<THREE.Vector3[]> {
		if (!markerPositions || markerPositions.length === 0) return [];

		this.clearProjectionRetry(volumeGroup);

		const userData: any = (volumeGroup as any).userData || ((volumeGroup as any).userData = {});
		const existingWorld: (THREE.Vector3 | null)[] = Array.isArray(userData.projectedBasePointsIndexed)
			? (userData.projectedBasePointsIndexed as (THREE.Vector3 | null)[]).map((p) => (p instanceof THREE.Vector3) ? p.clone() : null)
			: [];
		const existingLocal: (THREE.Vector3 | null)[] = Array.isArray(userData.projectedBasePointsIndexedLocal)
			? (userData.projectedBasePointsIndexedLocal as (THREE.Vector3 | null)[]).map((p) => (p instanceof THREE.Vector3) ? p.clone() : null)
			: [];
		const hits: (THREE.Vector3 | null)[] = new Array(markerPositions.length).fill(null);

		this.viewer.scene.scene.updateMatrixWorld(true);
		this.viewer.meshesGroup.updateMatrixWorld(true);
		this.viewer.volumesGroup.updateMatrixWorld(true);
		volumeGroup.updateMatrixWorld(true);

		const toWorldFromLocal = (local: THREE.Vector3 | null | undefined): THREE.Vector3 | null => {
			if (!(local instanceof THREE.Vector3)) return null;
			return volumeGroup.localToWorld(local.clone());
		};

		for (let i = 0; i < hits.length; i++) {
			if (existingWorld[i] instanceof THREE.Vector3) {
				hits[i] = (existingWorld[i] as THREE.Vector3).clone();
			} else if (existingLocal[i] instanceof THREE.Vector3) {
				hits[i] = toWorldFromLocal(existingLocal[i] as THREE.Vector3);
			}
		}

		const storeHits = () => {
			const localHits = hits.map(p => (p ? volumeGroup.worldToLocal(p.clone()) : null));
			const validPoints = hits.filter((p): p is THREE.Vector3 => !!p).map(p => p.clone());
			const validLocalPoints = localHits.filter((p): p is THREE.Vector3 => !!p).map(p => p.clone());

			if (validPoints.length > 0) {
				userData.projectedBasePoints = validPoints.map(p => p.clone());
				userData.projectedBasePointsLocal = validLocalPoints.map(p => p.clone());
			} else {
				delete userData.projectedBasePoints;
				delete userData.projectedBasePointsLocal;
			}

			userData.projectedBasePointsIndexed = hits.map(p => (p ? p.clone() : null));
			userData.projectedBasePointsIndexedLocal = localHits.map(p => (p ? p.clone() : null));
			userData.projectedBasePointsComplete = hits.length > 0 && hits.every(p => p instanceof THREE.Vector3);
			return { validPoints };
		};

		const globalTargets: THREE.Object3D[] = (this.viewer.meshes as any[]).filter(o => (o as any).visible) as any;
		if (globalTargets.length === 0) {
			const { validPoints } = storeHits();
			userData.projectedBasePointsComplete = false;
			if (attempt < VolumeManager.MAX_PROJECTION_RETRIES) {
				this.scheduleProjectionRetry(volumeGroup, markerPositions, attempt + 1);
			}
			return validPoints;
		}

		for (let i = 0; i < markerPositions.length; i++) {
			if (hits[i]) continue;
			const w = volumeGroup.localToWorld(markerPositions[i].clone());
			const hit = await this.verticalRaycastAtXYAgainst(w.x, w.y, [], globalTargets, w.z, 6, 120);
			if (hit) hits[i] = hit.point.clone();
		}

		const { validPoints } = storeHits();
		const missingCount = hits.filter(p => !p).length;
		if (missingCount > 0 && attempt < VolumeManager.MAX_PROJECTION_RETRIES) {
			this.scheduleProjectionRetry(volumeGroup, markerPositions, attempt + 1);
		}

		return validPoints;
	}

	private buildProjectionLines(volumeGroup: THREE.Group): void {
		const userData: any = (volumeGroup as any).userData || {};
		const raise = userData?.raiseOffset ?? 0;
		const basePointsLocal: THREE.Vector3[] = (userData?.projectedBasePointsLocal || []) as THREE.Vector3[];
		const basePointsWorld: THREE.Vector3[] = (userData?.projectedBasePoints || []) as THREE.Vector3[];
		const hasLocalPoints = Array.isArray(basePointsLocal) && basePointsLocal.length > 0;
		const basePoints: THREE.Vector3[] = hasLocalPoints ? basePointsLocal : basePointsWorld;
		const existingConnectors = volumeGroup.children?.find(c => c.name === "SelectionConnectors");

		if (!Number.isFinite(raise) || raise <= 0) return;
		if (!Array.isArray(basePoints) || basePoints.length === 0) {
			if (existingConnectors) this.removeConnectorGroup(volumeGroup, existingConnectors);
			return;
		}

		if (existingConnectors) this.removeConnectorGroup(volumeGroup, existingConnectors);

		volumeGroup.updateMatrixWorld(true);
		const connectorsGroup = new THREE.Group();
		connectorsGroup.name = "SelectionConnectors";
		connectorsGroup.visible = this.viewer.viewType == ViewType.View3D;
		const resolution = this.getCurrentResolution();

		for (const bp of basePoints) {
			if (!(bp instanceof THREE.Vector3)) continue;

			const startLocal = hasLocalPoints ? bp.clone() : volumeGroup.worldToLocal(bp.clone());
			const endLocal = startLocal.clone().add(new THREE.Vector3(0, 0, raise));
			const delta = endLocal.clone().sub(startLocal);
			const geo = new LineGeometry();
			geo.setPositions([0, 0, 0, delta.x, delta.y, delta.z]);
			const mat = new LineMaterial({ color: 0xffffff, linewidth: 1, resolution: resolution.clone() });
			mat.transparent = true;
			mat.opacity = 0.75;
			mat.depthTest = true;
			(mat as any).depthWrite = false;

			const line = new Line2(geo, mat);
			line.position.copy(startLocal);
			line.computeLineDistances();
			line.renderOrder = 10002;
			connectorsGroup.add(line);
		}

		if (connectorsGroup.children.length > 0) {
			volumeGroup.add(connectorsGroup);
			connectorsGroup.updateMatrixWorld(true);

			const label3D: any = userData?.areaLabel3D;
			if (label3D) {
				if (label3D.parent !== this.viewer.volumeLabelsGroup) {
					const worldPos = label3D.getWorldPosition(new THREE.Vector3());
					this.viewer.volumeLabelsGroup.add(label3D);
					label3D.position.copy(worldPos);
				}

				label3D.renderOrder = 999999;
				if (label3D.material) {
					label3D.material.depthTest = false;
					label3D.material.depthWrite = false;
				}
				label3D.traverse((child: any) => {
					if (child.type === "Sprite" || child.isSprite) {
						child.renderOrder = 999999;
						if (child.material) {
							child.material.depthTest = false;
							child.material.depthWrite = false;
						}
					}
				});
			}
		}
	}

	private removeConnectorGroup(volumeGroup: THREE.Group, connectorsGroup: THREE.Object3D): void {
		volumeGroup.remove(connectorsGroup);
		connectorsGroup.traverse((child: any) => {
			if (child.geometry) child.geometry.dispose();
			if (child.material) {
				if (Array.isArray(child.material)) {
					child.material.forEach((m: any) => m.dispose());
				} else {
					child.material.dispose();
				}
			}
		});
	}

	private clearProjectionRetry(volumeGroup: THREE.Group): void {
		const userData: any = (volumeGroup as any).userData;
		if (!userData) return;
		if (typeof userData.projectionRetryHandle === "number") {
			window.clearTimeout(userData.projectionRetryHandle);
		}
		delete userData.projectionRetryHandle;
		delete userData.projectionRetryMarkers;
		delete userData.projectionRetryAttempt;
	}

	private scheduleProjectionRetry(volumeGroup: THREE.Group, markerPositions: THREE.Vector3[], attempt: number): void {
		if (!markerPositions || markerPositions.length === 0) return;
		if (attempt > VolumeManager.MAX_PROJECTION_RETRIES) return;

		const userData: any = (volumeGroup as any).userData || ((volumeGroup as any).userData = {});
		if (typeof userData.projectionRetryHandle === "number") {
			window.clearTimeout(userData.projectionRetryHandle);
		}

		const markerClones = markerPositions.map(p => p.clone());
		const exponent = Math.max(0, attempt - 1);
		const delay = Math.min(4000, VolumeManager.PROJECTION_RETRY_BASE_DELAY_MS * Math.pow(2, exponent));

		userData.projectionRetryAttempt = attempt;
		userData.projectionRetryMarkers = markerClones;
		userData.projectionRetryHandle = window.setTimeout(async () => {
			if (!volumeGroup.parent) {
				this.clearProjectionRetry(volumeGroup);
				return;
			}

			userData.projectionRetryHandle = undefined;
			try {
				await this.projectPolygonToUnderlyingMesh(volumeGroup, markerClones, attempt);
				this.buildProjectionLines(volumeGroup);
				this.viewer.render();
			} catch (err) {
				console.warn("Failed to rebuild volume connectors on retry:", err);
			}
		}, delay);
	}

	private async verticalRaycastAtXYAgainst(
		x: number,
		y: number,
		volumeTargets: THREE.Object3D[] = [],
		globalTargets: THREE.Object3D[] = [],
		startZ?: number,
		step = 2,
		steps = 50
	): Promise<{ point: THREE.Vector3 } | null> {
		this.viewer.scene.scene.updateMatrixWorld(true);
		if (this.viewer.meshesGroup) this.viewer.meshesGroup.updateMatrixWorld(true);
		if (this.viewer.volumesGroup) this.viewer.volumesGroup.updateMatrixWorld(true);

		const targets: THREE.Object3D[] = [
			...(volumeTargets || []),
			...(globalTargets || []),
		].filter(Boolean);

		if (!targets.length) return null;

		const bbox = this.viewer.getBoundingBoxForObjects(targets as any);
		let z0: number;
		if (Number.isFinite(startZ as number)) {
			z0 = startZ as number;
		} else if (bbox) {
			z0 = bbox.max.z + Math.max(step * 2, 1);
		} else {
			z0 = 1e6;
		}

		if (bbox && z0 < bbox.max.z + 1) {
			z0 = bbox.max.z + Math.max(step * 2, 1);
		}

		const origin = new THREE.Vector3(x, y, z0);
		const direction = new THREE.Vector3(0, 0, -1);
		const maxDistance = bbox
			? Math.max((z0 - bbox.min.z) + step, step * steps + step)
			: (step * steps + step);
		const raycaster = new THREE.Raycaster(origin, direction, 0, maxDistance);
		const nexusTargets = targets.filter(t => (t as any) instanceof (NexusObject as any));
		let bestPoint: THREE.Vector3 | null = null;
		let bestDistance = Infinity;

		for (const obj of nexusTargets) {
			try {
				const p: THREE.Vector3 | null = await (obj as any as NexusObject).raycast(raycaster);
				if (p) {
					const d = raycaster.ray.origin.distanceTo(p);
					if (d < bestDistance) {
						bestDistance = d;
						bestPoint = p.clone();
					}
				}
			} catch (e) {
				// Ignore individual target errors; continue searching others.
			}
		}

		return bestPoint ? { point: bestPoint } : null;
	}

	private getCurrentResolution(): THREE.Vector2 {
		const renderAreaSize = this.viewer.renderer.getSize(new THREE.Vector2());
		return new THREE.Vector2(renderAreaSize.width, renderAreaSize.height);
	}

	private computeLocalScaleFactor(origin: THREE.Vector3, sourceCRS: string, targetCRS: string): number {
		try {
			const fwd = proj4(sourceCRS, targetCRS);
			const p0 = fwd.forward([origin.x, origin.y, origin.z]);
			const p1x = fwd.forward([origin.x + 1, origin.y, origin.z]);
			const p1y = fwd.forward([origin.x, origin.y + 1, origin.z]);
			const dx = Math.hypot((p1x[0] - p0[0]) || 0, (p1x[1] - p0[1]) || 0);
			const dy = Math.hypot((p1y[0] - p0[0]) || 0, (p1y[1] - p0[1]) || 0);
			return (Number.isFinite(dx) && dx > 0 && Number.isFinite(dy) && dy > 0)
				? (dx + dy) * 0.5
				: (Number.isFinite(dx) && dx > 0 ? dx : (Number.isFinite(dy) && dy > 0 ? dy : 1));
		} catch {
			return 1;
		}
	}

	public hideHoverTooltip(preserveCursor = false): void {
		if (!this.tooltipEl) return;
		this.tooltipEl.style.display = "none";
		this.cursorOverride = null;
		if (!preserveCursor && this.viewer.currentTool !== Tools.Selection && this.viewer.currentTool !== Tools.Annotation) {
			this.domElement.style.cursor = "";
		}
	}

	public async updateHoverTooltip(): Promise<void> {
		if (!this.tooltipEl) {
			this.cursorOverride = null;
			if (this.viewer.currentTool !== Tools.Selection && this.viewer.currentTool !== Tools.Annotation) {
				this.domElement.style.cursor = "";
			}
			return;
		}

		if (!this.volumes || this.volumes.length === 0) {
			this.hideHoverTooltip();
			return;
		}

		const now = performance.now();
		if (now - this.lastTooltipUpdate < VolumeManager.TOOLTIP_UPDATE_THROTTLE_MS) return;
		this.lastTooltipUpdate = now;

		const camera = this.viewer.scene.getActiveCamera();
		this.viewer.raycaster.setFromCamera(this.viewer.mousePosition, camera);

		let hitPoint: THREE.Vector3 | null = null;
		let hitVolume: THREE.Group | null = null;

		for (const volume of this.volumes) {
			if (!volume.visible) continue;

			const { before, after } = this.getBeforeAfterTargets(volume);

			let intersection = null;
			if (after && after.visible) {
				intersection = await (after as NexusObject).raycast(this.viewer.raycaster);
			} else if (before && before.visible) {
				intersection = await (before as NexusObject).raycast(this.viewer.raycaster);
			}

			if (intersection) {
				hitPoint = intersection;
				hitVolume = volume as THREE.Group;
				break;
			}
		}

		if (!hitPoint || !hitVolume) {
			this.hideHoverTooltip();
			if (this.hoveredVolumeId !== null) {
				this.hoveredVolumeId = null;
				this.viewer.onVolumeHover.emit(null);
			}
			return;
		}

		const currentVolumeId = hitVolume.uuid;
		if (this.hoveredVolumeId !== currentVolumeId) {
			this.hoveredVolumeId = currentVolumeId;
			this.viewer.onVolumeHover.emit(currentVolumeId);
		}

		const { before, after } = this.getBeforeAfterTargets(hitVolume);
		const x = hitPoint.x;
		const y = hitPoint.y;
		const raise = ((hitVolume as any).userData?.raiseOffset ?? 0) as number;

		try {
			const [beforeHit, afterHit] = await Promise.all([
				before ? this.verticalRaycastAtXYAgainst(x, y, [before], [], hitPoint.z + 50, 1, 10) : Promise.resolve(null),
				after ? this.verticalRaycastAtXYAgainst(x, y, [after], [], hitPoint.z + 50, 1, 10) : Promise.resolve(null),
			]);

			const beforeZ = beforeHit?.point?.z !== undefined ? (beforeHit.point.z - raise) : undefined;
			const afterZ = afterHit?.point?.z !== undefined ? (afterHit.point.z - raise) : undefined;
			const diffValue = (afterZ !== undefined ? afterZ : 0) - (beforeZ !== undefined ? beforeZ : 0);
			const unit = this.viewer.unit || "";
			const lenFmt = (v?: number) => (v === undefined ? "—" : (unit ? `${v.toFixed(3)} ${unit}` : v.toFixed(3)));
			const magnitude = Math.abs(diffValue);
			const deltaStr = unit ? `${magnitude.toFixed(3)} ${unit}` : magnitude.toFixed(3);
			const label = diffValue >= 0 ? "Fill" : "Cut";
			const lines: string[] = [];

			if (beforeZ !== undefined) lines.push(`Before: ${lenFmt(beforeZ)}`);
			if (afterZ !== undefined) lines.push(`After: ${lenFmt(afterZ)}`);
			lines.push(`${label}: ${deltaStr}`);

			if (lines.length === 0) {
				this.hideHoverTooltip();
				return;
			}

			this.tooltipEl.innerHTML = lines.join("<br/>");
			const rect = this.domElement.getBoundingClientRect();
			const px = ((this.viewer.mousePosition.x + 1) * 0.5) * rect.width;
			const py = ((-this.viewer.mousePosition.y + 1) * 0.5) * rect.height;
			this.tooltipEl.style.left = `${Math.round(px)}px`;
			this.tooltipEl.style.top = `${Math.round(py)}px`;
			this.tooltipEl.style.display = "block";
			this.cursorOverride = this.hoverCursor;
			if (this.viewer.currentTool !== Tools.Selection && this.viewer.currentTool !== Tools.Annotation) {
				this.domElement.style.cursor = this.hoverCursor;
			}
		} catch (e) {
			this.hideHoverTooltip();
		}
	}

	private createTooltip(): void {
		this.tooltipEl = document.createElement("div");
		this.tooltipEl.className = "volume-hover-tooltip";
		this.tooltipEl.style.position = "absolute";
		this.tooltipEl.style.pointerEvents = "none";
		this.tooltipEl.style.background = "rgba(0,0,0,0.75)";
		this.tooltipEl.style.color = "#fff";
		this.tooltipEl.style.padding = "6px 8px";
		this.tooltipEl.style.borderRadius = "4px";
		this.tooltipEl.style.fontSize = "12px";
		this.tooltipEl.style.lineHeight = "1.2";
		this.tooltipEl.style.zIndex = "10";
		this.tooltipEl.style.display = "none";
		this.tooltipEl.style.whiteSpace = "nowrap";
		this.tooltipEl.style.transform = "translate(10px, 10px)";
		if (getComputedStyle(this.domElement).position === "static") {
			this.domElement.style.position = "relative";
		}
		this.domElement.appendChild(this.tooltipEl);
	}
}
